import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Asterisk } from "@phosphor-icons/react";
import { scrollToId } from "@/lib/lenis";

const HERO_IMG =
  "https://images.unsplash.com/photo-1758518729685-f88df7890776?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwyfHxkaXZlcnNlJTIwcHJvZmVzc2lvbmFsJTIwYnVzaW5lc3MlMjB0ZWFtJTIwbWVldGluZyUyMG1vZGVybiUyMG9mZmljZXxlbnwwfHx8fDE3ODY0OTAwMzh8MA&ixlib=rb-4.1.0&q=85&w=1800";

const lineAnim = (i) => ({
  initial: { y: "115%" },
  animate: { y: "0%" },
  transition: { duration: 1, delay: 0.35 + i * 0.13, ease: [0.22, 1, 0.36, 1] },
});

const Hero = () => {
  const imgRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: imgRef,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["-12%", "12%"]);

  return (
    <section id="top" data-testid="hero-section" className="relative pt-40 md:pt-48 pb-16 md:pb-24 px-6 md:px-10">
      <div className="max-w-7xl mx-auto">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.25 }}
          className="text-xs md:text-sm tracking-[0.25em] uppercase font-bold text-terracotta mb-8"
        >
          HR &amp; Operations Consulting · Dallas–Fort Worth
        </motion.p>

        <h1 className="font-display font-bold tracking-tight leading-[1.02] text-[13.5vw] sm:text-6xl md:text-7xl lg:text-[5.6rem] text-ink">
          <span className="block overflow-hidden pb-1">
            <motion.span className="block" {...lineAnim(0)}>Better managers.</motion.span>
          </span>
          <span className="block overflow-hidden pb-1">
            <motion.span className="block" {...lineAnim(1)}>Stronger teams.</motion.span>
          </span>
          <span className="block overflow-hidden pb-2">
            <motion.span className="block" {...lineAnim(2)}>
              Better{" "}
              <span className="font-serif italic font-medium text-terracotta">workplaces.</span>
            </motion.span>
          </span>
        </h1>

        <div className="mt-10 md:mt-14 grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9 }}
            className="md:col-span-6 text-base md:text-lg leading-relaxed text-ink-soft max-w-xl"
          >
            We help organizations develop capable managers, strengthen workplace
            communication, and build the skills teams need to work effectively together.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.05 }}
            className="md:col-span-6 flex flex-wrap gap-4 md:justify-end"
          >
            <button
              data-testid="hero-cta-consultation"
              onClick={() => scrollToId("#contact")}
              className="rounded-full bg-ink text-white font-semibold px-8 py-4 text-sm md:text-base hover:bg-terracotta hover:scale-105 transition-[background-color,transform] duration-300"
            >
              Schedule a Consultation
            </button>
            <button
              data-testid="hero-cta-services"
              onClick={() => scrollToId("#workshops")}
              className="rounded-full border border-ink/20 text-ink font-semibold px-8 py-4 text-sm md:text-base hover:border-terracotta hover:text-terracotta transition-colors duration-300"
            >
              Explore Our Services
            </button>
          </motion.div>
        </div>

        <div ref={imgRef} className="relative mt-16 md:mt-24">
          <motion.div
            initial={{ clipPath: "inset(100% 0 0 0)" }}
            animate={{ clipPath: "inset(0% 0 0 0)" }}
            transition={{ duration: 1.2, delay: 1.15, ease: [0.22, 1, 0.36, 1] }}
            className="relative overflow-hidden rounded-[2rem] md:rounded-[2.5rem] h-[46vh] md:h-[72vh]"
          >
            <motion.img
              src={HERO_IMG}
              alt="Diverse business team collaborating in a modern office"
              data-testid="hero-image"
              style={{ y, scale: 1.18 }}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-ink/25 via-transparent to-transparent" />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.6, rotate: -30 }}
            animate={{ opacity: 1, scale: 1, rotate: 0 }}
            transition={{ duration: 0.9, delay: 1.7, ease: [0.22, 1, 0.36, 1] }}
            className="absolute -top-10 right-6 md:-top-14 md:right-14 w-24 h-24 md:w-36 md:h-36 rounded-full bg-cream flex items-center justify-center shadow-[0_8px_30px_rgb(0,0,0,0.08)]"
          >
            <div className="absolute inset-0 animate-spin-slow">
              <svg viewBox="0 0 100 100" className="w-full h-full">
                <defs>
                  <path id="circlePath" d="M 50,50 m -36,0 a 36,36 0 1,1 72,0 a 36,36 0 1,1 -72,0" />
                </defs>
                <text className="fill-ink" style={{ fontSize: 11, letterSpacing: 3, fontFamily: "Manrope", fontWeight: 700 }}>
                  <textPath href="#circlePath">PEOPLE FIRST • ALWAYS •</textPath>
                </text>
              </svg>
            </div>
            <Asterisk size={30} weight="bold" className="text-terracotta" />
          </motion.div>
        </div>

        <motion.button
          data-testid="hero-scroll-hint"
          onClick={() => scrollToId("#workshops")}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2, duration: 0.8 }}
          className="mt-10 flex items-center gap-3 text-xs tracking-[0.25em] uppercase font-bold text-ink-soft hover:text-terracotta transition-colors"
          aria-label="Scroll to workshops and training"
        >
          <motion.span animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}>
            <ArrowDown size={16} weight="bold" />
          </motion.span>
          Scroll
        </motion.button>
      </div>
    </section>
  );
};

export default Hero;
